import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { visuallyHidden } from '@mui/utils';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import Form from './Form';
import Show from './Show';
import DeleteConfirmation from '../../components/DeleteConfirmation';
import ProductService from '../../../services/product.service';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import ImageIcon from '@mui/icons-material/Image';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const headCells = [
  {
    id: 'image',
    numeric: false,
    label: 'Image',
  },
  {
    id: 'name',
    numeric: false,
    label: 'Nom',
  },
  {
    id: 'description',
    numeric: false,
    label: 'Description',
  },
  {
    id: 'price',
    numeric: true,
    label: 'Prix',
  },
  {
    id: 'is_goodie',
    numeric: false,
    label: 'Offert',
  },
  {
    id: 'event',
    numeric: false,
    label: 'Événement',
  },
  {
    id: 'category',
    numeric: false,
    label: 'Catégorie',
  },
  {
    id: 'product_sale_points',
    numeric: false,
    label: 'Points de vente',
  },
  {
    id: 'show_order',
    numeric: true,
    label: 'Ordre',
  },
  {
    id: 'actions',
    numeric: false,
    label: '',
  },
];

function EnhancedTableHead(props) {
  const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } =
    props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding='normal'
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

function EnhancedTableToolbar({ searchQuery, onSearchChange, onAddClick, onDownloadClick }) {
  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        display: 'flex',
        justifyContent: 'space-between',
        borderBottom: '1px solid #f0f0f0'
      }}
    >
      <Typography
        variant="h6"
        component="div"
        sx={{ 
          fontSize: '20px',
          fontWeight: 'bold'
        }}
      >
        Produits
      </Typography>
      <div className="d-flex align-items-center gap-3">
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            size='small'
            value={searchQuery}
            onChange={onSearchChange}
            placeholder="Rechercher..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" >
                  <SearchIcon sx={{ color: '#666' }} fontSize='small' />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: 'white',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '& fieldset': {
                  borderColor: '#e0e0e0',
                },
              }
            }}
          />
        </Box>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          sx={{ 
            borderColor: 'primary.main',
            color: 'primary.main',
            borderRadius: '8px',
            textTransform: 'none',
            '&:hover': {
              borderColor: '#333',
              backgroundColor: '#f5f5f5'
            }
          }}
          onClick={onDownloadClick}
        >
          Télécharger Excel
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ 
            backgroundColor: 'primary.main',
            borderRadius: '8px',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#333'
            }
          }}
          onClick={onAddClick}
        >
          Ajouter
        </Button>
      </div>
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  searchQuery: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onAddClick: PropTypes.func.isRequired,
  onDownloadClick: PropTypes.func.isRequired,
};

function ActionMenu({ row, onEditClick, onShowClick, onDeleteClick }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const open = Boolean(anchorEl);
  
  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = (event) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const handleEdit = (event) => {
    handleClose(event);
    onEditClick(row);
  };

  const handleShow = (event) => {
    handleClose(event);
    onShowClick(row);
  };

  const handleDeleteClick = (event) => {
    handleClose(event);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    onDeleteClick(row);
    setDeleteConfirmOpen(false);
  };

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem onClick={handleShow} sx={{ color: 'primary.main', gap: 1}}> 
          <InfoIcon /> <span style={{ marginTop: '4px' }}>Détails</span>
        </MenuItem>
        <MenuItem onClick={handleEdit} sx={{ color: 'orange', gap: 1}}>
          <EditIcon /> <span style={{ marginTop: '4px' }}>Modifier</span>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'red', gap: 1}}>
          <DeleteIcon /> <span style={{ marginTop: '4px' }}>Supprimer</span>
        </MenuItem>
      </Menu>

      <DeleteConfirmation
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Supprimer le produit"
        content="Êtes-vous sûr de vouloir supprimer ce produit ?"
      />
    </>
  );
}

export default function List() {
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('');
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [isShowOpen, setIsShowOpen] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success'
  });

  React.useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await ProductService.list();
      if (response) {
        setRows(response);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erreur lors du chargement des produits',
        severity: 'error'
      });
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleShowClose = () => {
    setIsShowOpen(false);
    setSelectedItem(null);
  };

  const handleAddClick = () => {
    setSelectedItem(null);
    setIsFormOpen(true);
  };

  const handleEditClick = (product) => {
    setSelectedItem(product);
    setIsFormOpen(true);
  };

  const handleShowClick = (product) => {
    setSelectedItem(product);
    setIsShowOpen(true);
  };

  const handleDeleteClick = async (product) => {
    try {
      await ProductService.delete(product.id);
      fetchProducts();
      setSnackbar({
        open: true,
        message: 'Produit supprimé avec succès',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erreur lors de la suppression du produit',
        severity: 'error'
      });
    }
  };

  const handleDownloadExcel = async () => {
    try {
      await ProductService.exportProductSalesExcel();
      setSnackbar({
        open: true,
        message: 'Téléchargement du fichier Excel réussi',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Erreur lors du téléchargement du fichier Excel',
        severity: 'error'
      });
    }
  };

  const handleFormClose = async (formData, onError) => {
    if (formData) {
      try {
        if (selectedItem) {
          await ProductService.update({
            ...formData,
            id: selectedItem.id
          });
          setSnackbar({
            open: true,
            message: 'Produit mis à jour avec succès',
            severity: 'success'
          });
          setIsFormOpen(false);
          setSelectedItem(null);
          fetchProducts();
        } else {
          await ProductService.create(formData);
          setSnackbar({
            open: true,
            message: 'Produit créé avec succès',
            severity: 'success'
          });
          setIsFormOpen(false);
          setSelectedItem(null);
          fetchProducts();
        }
      } catch (error) {
        // Si c'est une erreur de validation, passer les erreurs au formulaire
        if (error.validationErrors && error.validationErrors.length > 0 && onError) {
          onError(error.validationErrors);
        } else {
          setSnackbar({
            open: true,
            message: error.message || 'Erreur lors de l\'enregistrement du produit',
            severity: 'error'
          });
        }
        // Ne pas fermer le modal en cas d'erreur
      }
    } else {
      // Fermeture normale (annulation)
      setIsFormOpen(false);
      setSelectedItem(null);
    }
  };
  
  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({...snackbar, open: false});
  };

  const visibleRows = React.useMemo(
    () => {
      if (!rows) return [];
      
      const filteredRows = [...rows].filter((row) => 
        row.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      
      return filteredRows
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    },
    [order, orderBy, page, rowsPerPage, searchQuery, rows],
  );

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - (rows?.length || 0)) : 0;

  return (
    <Box sx={{ width: '100%', backgroundColor: '#f8f9fa' }}>
      <Paper 
        sx={{ 
          width: '100%', 
          mb: 2,
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
        }}
      >
        <EnhancedTableToolbar 
          searchQuery={searchQuery}
          onSearchChange={handleSearch}
          onAddClick={handleAddClick}
          onDownloadClick={handleDownloadExcel}
        />
        
        <TableContainer>
          <Table
            sx={{ 
              minWidth: 750,
              '& .MuiTableCell-root': {
                borderBottom: '1px solid #f0f0f0'
              }
            }}
            aria-labelledby="tableTitle"
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows?.length || 0}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const isItemSelected = selected.includes(row.id);
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                    hover
                    onClick={(event) => handleClick(event, row.id)}
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.id}
                    selected={isItemSelected}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      {row.image ? (
                        <img
                          src={`${process.env.REACT_APP_BASE_URL}/${row.image}`}
                          alt={row.name}
                          style={{
                            width: '50px',
                            height: '50px',
                            objectFit: 'cover',
                            borderRadius: '4px'
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: '50px',
                            height: '50px',
                            backgroundColor: '#f0f0f0',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <ImageIcon sx={{ color: '#999' }} />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                    >
                      {row.name}
                    </TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell align="right">
                      {row.price.toLocaleString('fr-FR')} FCFA
                    </TableCell>
                    <TableCell>
                      {row.is_goodie ? (
                        <span style={{ 
                          backgroundColor: '#4caf50', 
                          color: 'white', 
                          padding: '4px 8px', 
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}>
                          Oui
                        </span>
                      ) : (
                        <span style={{ 
                          backgroundColor: '#f44336', 
                          color: 'white', 
                          padding: '4px 8px', 
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}>
                          Non
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{row.event?.name}</TableCell>
                    <TableCell>{row.category?.name}</TableCell>
                    <TableCell>
                      {row.product_sale_points?.map((point) => (
                        <div key={point.id}>
                          {point.salePoint.name} ({point.quantity})
                        </div>
                      ))}
                    </TableCell>
                    <TableCell align="right">
                      {row.show_order || 0}
                    </TableCell>
                    <TableCell align="right">
                      <ActionMenu 
                        row={row} 
                        onEditClick={handleEditClick} 
                        onShowClick={handleShowClick}
                        onDeleteClick={handleDeleteClick}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={10} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows?.filter(row => 
            row.name.toLowerCase().includes(searchQuery.toLowerCase())
          ).length || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
          sx={{
            "& .MuiTablePagination-toolbar": {
              backgroundColor: "#f5f5f5",
              display: 'flex',
              alignItems: 'center',
            },
            "& .MuiTablePagination-actions": {
              color: "blue",
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-input": {
              paddingRight: '16px',
            },
          }}
        />
      </Paper>

      <Form
        open={isFormOpen}
        onClose={() => handleFormClose(null)}
        initialData={selectedItem}
        onSubmit={(formData, onError) => handleFormClose(formData, onError)}
        onCancel={() => handleFormClose(null)}
      />
      <Show
        open={isShowOpen}
        onClose={handleShowClose}
        itemData={selectedItem}
      />

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
